import { useAuth } from '../contexts/AuthContext';
import { useAppVersion } from '../hooks/useAppVersion';
import { useEffect } from 'react';

export function Profile() {
  const { user, logout } = useAuth();
  const { currentVersion, checkForUpdates } = useAppVersion();

  useEffect(() => {
    checkForUpdates();
  }, []);

  if (!user) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const roleBadgeColor = {
    superadmin: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
    admin: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    researcher: 'bg-green-500/10 text-green-500 border-green-500/20',
    viewer: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-white px-6 py-4">
        <h1 className="text-2xl font-bold">Profile</h1>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-scroll p-6 pb-32" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
        <div className="max-w-2xl mx-auto space-y-6 pb-24">
          {/* User Info Card */}
          <div className="bg-white rounded-xl border border-border p-6">
            <div className="flex items-start gap-4 mb-6">
              {user.profileImageUrl ? (
                <img
                  src={user.profileImageUrl}
                  alt={user.firstName || user.email}
                  className="w-20 h-20 rounded-full"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary">
                    {user.firstName?.[0] || user.email[0].toUpperCase()}
                  </span>
                </div>
              )}
              <div className="flex-1">
                <h2 className="text-xl font-bold">
                  {user.firstName && user.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user.firstName || 'User'}
                </h2>
                <p className="text-muted-foreground">{user.email}</p>
                <div className="mt-2">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${roleBadgeColor[user.role]}`}>
                    {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={logout}
              className="w-full px-4 py-2 bg-destructive/10 text-destructive border border-destructive/20 rounded-lg font-medium hover:bg-destructive/20 transition-colors"
            >
              Logout
            </button>
          </div>

          {/* Version Info Card */}
          <div className="bg-white rounded-xl border border-border p-6">
            <h3 className="text-lg font-bold mb-4">App Version</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-border">
                <span className="text-sm text-muted-foreground">Version</span>
                <span className="font-mono font-medium">
                  {currentVersion ? `v${currentVersion.version}` : 'Loading...'}
                </span>
              </div>
              {currentVersion?.buildId && (
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Build ID</span>
                  <span className="font-mono text-sm">
                    {currentVersion.buildId}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-muted-foreground">Last Updated</span>
                <span className="text-sm">
                  {currentVersion?.timestamp
                    ? new Date(currentVersion.timestamp).toLocaleDateString()
                    : 'Unknown'}
                </span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Please include the version number when reporting bugs
            </p>
          </div>

          {/* Settings Card (Placeholder) */}
          <div className="bg-white rounded-xl border border-border p-6">
            <h3 className="text-lg font-bold mb-4">Settings</h3>
            <div className="space-y-3">
              <button className="w-full flex items-center justify-between py-3 px-4 bg-secondary rounded-lg hover:bg-secondary/80 transition-colors">
                <span className="font-medium">Notifications</span>
                <svg className="w-5 h-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
              <button className="w-full flex items-center justify-between py-3 px-4 bg-secondary rounded-lg hover:bg-secondary/80 transition-colors">
                <span className="font-medium">Privacy</span>
                <svg className="w-5 h-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
              <button className="w-full flex items-center justify-between py-3 px-4 bg-secondary rounded-lg hover:bg-secondary/80 transition-colors">
                <span className="font-medium">About</span>
                <svg className="w-5 h-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
